import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCallAPI } from 'hooks/useCallAPI';
import {
  SiteAnnouncement,
  SiteAnnouncementStatus
} from 'core/siteAnnouncement/SiteAnnouncement';
import _ from 'lodash';
import {
  ColumnDefinition,
  getCustomRenderColumn,
  getStatusDesColumn,
  renderColumn
} from 'components/TableColumn/TableColumn';
import i18n from 'i18n';
import styles from './siteAnnouncementList.module.scss';
import { useCoreContext } from 'contexts/coreContext';
import {
  renderSiteAnnouncementAdminNameColumn,
  renderSiteAnnouncementNameIdWithDetailColumn
} from './components/SiteAnnouncementNameIdWithDetailColumn';
import {
  renderSiteAnnouncementAttachmentsColumn,
  renderSiteAnnouncementImageColumn
} from './components/SiteAnnouncementMediaColumns';
import {
  DefaultSiteAnnouncementManager,
  SiteAnnouncementManager
} from 'core/siteAnnouncement/SiteAnnouncementManager';
import { AgencyManager, DefaultAgencyManager } from 'core';
import { SortOrder, TableChangeHandler } from 'components/common/Table/types';
import { toast } from 'react-toastify';
import { renderSiteAnnouncementContent } from 'components/SiteAnnouncement/SiteAnnouncementContent';

type BatchOperation = {
  text: string;
  enable: boolean;
  hint: string;
  onClick: () => void;
};

export type SiteAnnouncementListModel = {
  loading: boolean;
  isAdmin: boolean;
  searchString: string;
  filteredAnnouncements: SiteAnnouncement[];
  selectedAnnouncements: number[];
  columns: ColumnDefinition[];
  batchOperations?: {
    operation: BatchOperation;
  }[];
  onHandleSearch: (searchString: string) => void;
  onHandleSelect: (id: number | string, select: boolean) => void;
  onHandleSelectPage: (select: boolean) => void;
  onClearSelect: () => void;
  onTableChange: TableChangeHandler<SiteAnnouncement>;
  markAnnouncementsAsRead: () => void;
  getAnnouncementRowClasses?: (announcement: SiteAnnouncement) => string;
  detailAnnouncement?: SiteAnnouncement;
  onShowDetail: (announcement: SiteAnnouncement) => void;
  onHideDetail: () => void;
  showDeleteConfirmModal: boolean;
  deleteAnnouncementIds: number[];
  onShowDeleteConfirmModal: (ids: number[]) => void;
  onHideDeleteConfirmModal: () => void;
  onConfirmDelete: () => void;
};

export enum SiteAnnouncementListColumn {
  ID = 'id',
  NAME = 'name',
  IMAGE_URL = 'imageUrl',
  CONTENT = 'content',
  ATTACHMENT_URLS = 'attachmentUrls',
  AGENCY_IDS = 'agencyIds',
  PUBLISH_TIME = 'publishTime',
  STATUS = 'status',
  CREATOR = 'creator'
}

const defaultSiteAnnouncementManager: SiteAnnouncementManager =
  new DefaultSiteAnnouncementManager();
const defaultAgencyManager: AgencyManager = new DefaultAgencyManager();
export const SIZE_PER_PAGE = 10;
export const useSiteAnnouncementListModel = (
  autoOpenAnnouncementId?: number
): SiteAnnouncementListModel => {
  const { loading, callAPIs } = useCallAPI();
  const [searchString, setSearchString] = useState<string>('');
  const [siteAnnouncements, setSiteAnnouncements] = useState<
    SiteAnnouncement[]
  >([]);
  const [selectedAnnouncements, setSelectedAnnouncements] = useState<number[]>(
    []
  );
  const [agencyOptions, setAgencyOptions] = useState<SelectOptions[]>([]);
  const [detailAnnouncement, setDetailAnnouncement] = useState<
    SiteAnnouncement | undefined
  >();
  const core = useCoreContext();
  const authenticationManager = core?.authenticationManager;
  const isAdmin = authenticationManager?.actor?.roleName === 'ADMIN';
  const [page, setPage] = useState<number>(1);
  const [publishTimeSort, setPublishTimeSort] = useState<
    SortOrder | undefined
  >();
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] =
    useState<boolean>(false);
  const [deleteAnnouncementIds, setDeleteAnnouncementIds] = useState<number[]>(
    []
  );

  const sortByPublishTime = useCallback(
    (a: SiteAnnouncement, b: SiteAnnouncement, sortOrder: SortOrder) => {
      const aTime = new Date(a.publishTime).getTime();
      const bTime = new Date(b.publishTime).getTime();
      if (aTime < bTime) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aTime > bTime) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    },
    []
  );

  // Order the first page so unread announcements come first (the rest keep the
  // server order). Reused whenever the list is (re)fetched so the ordering is
  // consistent after batch operations and deletion too.
  const sortUnreadFirstOnFirstPage = useCallback(
    (results: SiteAnnouncement[]) => {
      if (!authenticationManager) {
        return results;
      }
      const firstPageAnnouncements = results.slice(0, SIZE_PER_PAGE);
      firstPageAnnouncements.sort((a, b) => {
        const isARead = authenticationManager.hasReadAnnouncement(a);
        const isBRead = authenticationManager.hasReadAnnouncement(b);
        if (!isARead && isBRead) {
          return -1;
        }
        if (!isBRead && isARead) {
          return 1;
        }
        return 0;
      });
      return [...firstPageAnnouncements, ...results.slice(SIZE_PER_PAGE)];
    },
    [authenticationManager]
  );

  useEffect(() => {
    if (!authenticationManager) {
      return;
    }
    callAPIs(
      [() => defaultSiteAnnouncementManager.getSiteAnnouncements()],
      (results: SiteAnnouncement[]) => {
        setSiteAnnouncements(sortUnreadFirstOnFirstPage(results));
      }
    );
  }, [authenticationManager, sortUnreadFirstOnFirstPage, callAPIs]);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }
    callAPIs(
      [() => defaultAgencyManager.getAgenciesOptions()],
      (options: SelectOptions[]) => {
        setAgencyOptions(options);
      }
    );
  }, [isAdmin, callAPIs]);

  const agencyNameById = useMemo(() => {
    return new Map(
      agencyOptions.map((option) => [String(option.value), option.label])
    );
  }, [agencyOptions]);

  const onShowDetail = useCallback(
    (announcement: SiteAnnouncement) => {
      setDetailAnnouncement(announcement);
      authenticationManager?.markAnnouncementAsRead(announcement);
      window.dispatchEvent(
        new CustomEvent('announcements-read', {
          detail: { announcementIdList: [announcement.id] }
        })
      );
    },
    [authenticationManager]
  );

  const onHideDetail = useCallback(() => {
    setDetailAnnouncement(undefined);
  }, []);

  const hasAutoOpenedRef = useRef<boolean>(false);
  useEffect(() => {
    if (hasAutoOpenedRef.current || autoOpenAnnouncementId === undefined) {
      return;
    }
    const target = siteAnnouncements.find(
      (announcement) => announcement.id === autoOpenAnnouncementId
    );
    if (target) {
      hasAutoOpenedRef.current = true;
      onShowDetail(target);
    }
  }, [autoOpenAnnouncementId, siteAnnouncements, onShowDetail]);

  const getColumnDefinition = useCallback(
    (column: SiteAnnouncementListColumn) => {
      return {
        dataField: column,
        text: i18n.t(`siteAnnouncementList.headers.${column}`),
        classes: styles[column]
      };
    },
    []
  );

  const adminColumns = useMemo(() => {
    return _.compact([
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.NAME),
        renderSiteAnnouncementAdminNameColumn
      ),
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.IMAGE_URL),
        renderSiteAnnouncementImageColumn
      ),
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.CONTENT),
        (record: SiteAnnouncement) =>
          renderSiteAnnouncementContent(record.content, true, 2)
      ),
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.ATTACHMENT_URLS),
        renderSiteAnnouncementAttachmentsColumn
      ),
      renderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.AGENCY_IDS),
        (value?: string[]) =>
          value && value.length > 0
            ? value
                .map(
                  (agencyId) => agencyNameById.get(String(agencyId)) ?? agencyId
                )
                .join(', ')
            : i18n.t('siteAnnouncementList.labels.allAgencies')
      ),
      {
        ...getColumnDefinition(SiteAnnouncementListColumn.PUBLISH_TIME),
        sort: true
      },
      getStatusDesColumn(
        getColumnDefinition(SiteAnnouncementListColumn.STATUS),
        (announcement: SiteAnnouncement) => {
          switch (announcement.status) {
            case SiteAnnouncementStatus.PUBLISHED:
              return {
                des: i18n.t('siteAnnouncementList.labels.published'),
                color: 'theme1'
              };
            case SiteAnnouncementStatus.UNPUBLISHED:
              return {
                des: i18n.t('siteAnnouncementList.labels.unpublished'),
                color: 'light'
              };
            case SiteAnnouncementStatus.INACTIVE:
              return {
                des: i18n.t('siteAnnouncementList.labels.inactive'),
                color: 'danger'
              };
            default:
              return { des: '', color: '' };
          }
        }
      ),
      getColumnDefinition(SiteAnnouncementListColumn.CREATOR)
    ]);
  }, [getColumnDefinition, agencyNameById]);

  const normalColumns = useMemo(() => {
    return _.compact([
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.NAME),
        renderSiteAnnouncementNameIdWithDetailColumn(onShowDetail)
      ),
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.IMAGE_URL),
        renderSiteAnnouncementImageColumn
      ),
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.CONTENT),
        (record: SiteAnnouncement) =>
          renderSiteAnnouncementContent(record.content, true, 2)
      ),
      getCustomRenderColumn(
        getColumnDefinition(SiteAnnouncementListColumn.ATTACHMENT_URLS),
        renderSiteAnnouncementAttachmentsColumn
      ),
      {
        ...getColumnDefinition(SiteAnnouncementListColumn.PUBLISH_TIME),
        sort: true
      }
    ]);
  }, [getColumnDefinition, onShowDetail]);

  const columns = useMemo(
    () => (isAdmin ? adminColumns : normalColumns),
    [isAdmin, adminColumns, normalColumns]
  );

  const filteredAnnouncements = useMemo(() => {
    return siteAnnouncements.filter((announcement) => {
      const nameMatch = _.includes(
        announcement.name.toLowerCase(),
        searchString.toLowerCase()
      );
      const contentMatch = _.includes(
        announcement.content.toLowerCase(),
        searchString.toLowerCase()
      );
      const agencyIdsMatch = isAdmin
        ? _.some(announcement.agencyIds || [], (agencyId) => {
            const keyword = searchString.toLowerCase();
            const agencyName = agencyNameById.get(String(agencyId));
            return (
              _.includes(agencyId.toString(), keyword) ||
              (!!agencyName && _.includes(agencyName.toLowerCase(), keyword))
            );
          }) ||
          (_.isEmpty(announcement.agencyIds) &&
            _.includes(
              i18n.t('siteAnnouncementList.labels.allAgencies').toLowerCase(),
              searchString.toLowerCase()
            ))
        : false;
      return nameMatch || contentMatch || agencyIdsMatch;
    });
  }, [isAdmin, siteAnnouncements, searchString, agencyNameById]);

  const onHandleSearch = useCallback((searchString: string) => {
    setSearchString(searchString);
  }, []);

  const onHandleSelect = useCallback((id: number | string, select: boolean) => {
    setSelectedAnnouncements((prev) =>
      select
        ? _.uniq([...prev, +id])
        : prev.filter((p) => p.toString() !== id.toString())
    );
  }, []);

  const onHandleSelectPage = useCallback(
    (select: boolean) => {
      const targetId = siteAnnouncements.map((row) => row.id);
      setSelectedAnnouncements((prev) =>
        select
          ? _.uniq([...prev, ...targetId])
          : prev.filter((p) => !targetId.includes(p))
      );
    },
    [siteAnnouncements]
  );

  const onClearSelect = useCallback(() => {
    setSelectedAnnouncements([]);
  }, []);

  // 處理批次操作後的通用回調函數
  const handleBatchOperationSuccess = useCallback(
    (operationType: 'activate' | 'deactivate' | 'delete') => {
      callAPIs(
        [() => defaultSiteAnnouncementManager.getSiteAnnouncements()],
        (results: SiteAnnouncement[]) => {
          setSiteAnnouncements(sortUnreadFirstOnFirstPage(results));
          setSelectedAnnouncements([]);

          // 顯示成功提示訊息
          switch (operationType) {
            case 'activate':
              toast.success(
                i18n.t('siteAnnouncementList.messages.activateSuccess')
              );
              break;
            case 'deactivate':
              toast.success(
                i18n.t('siteAnnouncementList.messages.deactivateSuccess')
              );
              break;
            case 'delete':
              toast.success(
                i18n.t('siteAnnouncementList.messages.deleteSuccess')
              );
              break;
          }
        }
      );
    },
    [callAPIs, sortUnreadFirstOnFirstPage]
  );

  // 啟用
  const createActivateOperation = useCallback(() => {
    return {
      operation: {
        text: i18n.t('siteAnnouncementList.buttons.activate'),
        hint: '',
        enable: true,
        onClick: () => {
          callAPIs(
            [
              () =>
                defaultSiteAnnouncementManager.activateAnnouncements(
                  selectedAnnouncements
                )
            ],
            () => handleBatchOperationSuccess('activate')
          );
        }
      }
    };
  }, [selectedAnnouncements, callAPIs, handleBatchOperationSuccess]);

  // 停用
  const createDeactivateOperation = useCallback(() => {
    return {
      operation: {
        text: i18n.t('siteAnnouncementList.buttons.deactivate'),
        hint: '',
        enable: true,
        onClick: () => {
          callAPIs(
            [
              () =>
                defaultSiteAnnouncementManager.deactivateAnnouncements(
                  selectedAnnouncements
                )
            ],
            () => handleBatchOperationSuccess('deactivate')
          );
        }
      }
    };
  }, [selectedAnnouncements, callAPIs, handleBatchOperationSuccess]);

  // 共用刪除邏輯
  const executeDelete = useCallback(
    (ids: number[]) => {
      callAPIs(
        [() => defaultSiteAnnouncementManager.deleteAnnouncements(ids)],
        () => {
          // Refresh the list after deletion
          callAPIs(
            [() => defaultSiteAnnouncementManager.getSiteAnnouncements()],
            (results: SiteAnnouncement[]) => {
              setSiteAnnouncements(sortUnreadFirstOnFirstPage(results));
              setSelectedAnnouncements([]);
              toast.success(
                i18n.t('siteAnnouncementList.messages.deleteSuccess')
              );
            }
          );
        },
        (error: any) => {
          console.error('Delete announcement error:', error);
          toast.error(i18n.t('siteAnnouncementList.messages.deleteError'));
        }
      );
    },
    [callAPIs, sortUnreadFirstOnFirstPage]
  );

  // 刪除
  const createDeleteOperation = useCallback(
    (selectedAnnouncementsData: SiteAnnouncement[]) => {
      // 檢查選中項中是否所有項都可刪除（即狀態為 UNPUBLISHED）
      const canDeleteAll = selectedAnnouncementsData.every(
        (announcement) =>
          announcement.status === SiteAnnouncementStatus.UNPUBLISHED
      );

      return {
        operation: {
          text: i18n.t('common.buttons.delete'),
          hint: canDeleteAll
            ? ''
            : i18n.t('siteAnnouncementList.hints.cannotDelete'),
          enable: canDeleteAll,
          onClick: () => {
            setDeleteAnnouncementIds(selectedAnnouncements);
            setShowDeleteConfirmModal(true);
          }
        }
      };
    },
    [selectedAnnouncements]
  );

  const batchOperations = useMemo(() => {
    if (selectedAnnouncements.length === 0) {
      return [];
    }

    const selectedAnnouncementsData = siteAnnouncements.filter((announcement) =>
      selectedAnnouncements.includes(announcement.id)
    );

    return [
      createActivateOperation(),
      createDeactivateOperation(),
      createDeleteOperation(selectedAnnouncementsData)
    ];
  }, [
    selectedAnnouncements,
    siteAnnouncements,
    createActivateOperation,
    createDeactivateOperation,
    createDeleteOperation
  ]);

  const firstPagesAnnouncements = useMemo(() => {
    return siteAnnouncements.slice(0, SIZE_PER_PAGE);
  }, [siteAnnouncements]);

  const sortedAnnouncements = useMemo(() => {
    if (!publishTimeSort) {
      return filteredAnnouncements;
    }
    return [...filteredAnnouncements].sort((a, b) => {
      return sortByPublishTime(a, b, publishTimeSort);
    });
  }, [filteredAnnouncements, publishTimeSort, sortByPublishTime]);

  const markAnnouncementsAsRead = useCallback(() => {
    if (!authenticationManager || isAdmin) {
      return;
    }

    const currentPageFirstAnnouncementIndex = (page - 1) * SIZE_PER_PAGE;
    const currentPageFinalAnnouncementIndex =
      currentPageFirstAnnouncementIndex + SIZE_PER_PAGE - 1;
    // 目前頁面的通知
    const currentPageAnnouncements = sortedAnnouncements.slice(
      currentPageFirstAnnouncementIndex,
      currentPageFinalAnnouncementIndex + 1
    );

    // 只更新最原始第一頁通知訊息的讀取狀態
    const needToMarkAsReadAnnouncements = currentPageAnnouncements.filter(
      (announcement: SiteAnnouncement) => {
        return (
          firstPagesAnnouncements.find(
            (firstPageAnnouncement: SiteAnnouncement) => {
              return firstPageAnnouncement.id === announcement.id;
            }
          ) !== undefined
        );
      }
    );

    needToMarkAsReadAnnouncements.forEach((announcement: SiteAnnouncement) => {
      authenticationManager.markAnnouncementAsRead(announcement);
    });
    window.dispatchEvent(
      new CustomEvent('announcements-read', {
        detail: {
          announcementIdList: needToMarkAsReadAnnouncements.map(
            (announcement) => announcement.id
          )
        }
      })
    );
  }, [
    page,
    sortedAnnouncements,
    firstPagesAnnouncements,
    authenticationManager,
    isAdmin
  ]);

  const getAnnouncementRowClasses = useCallback(
    (announcement: SiteAnnouncement): string => {
      if (!authenticationManager || isAdmin) {
        return '';
      }
      if (
        !firstPagesAnnouncements.find(
          (firstPageAnnouncement: SiteAnnouncement) => {
            return firstPageAnnouncement.id === announcement.id;
          }
        )
      ) {
        return '';
      }
      return authenticationManager.hasReadAnnouncement(announcement)
        ? ''
        : styles.unread;
    },
    [isAdmin, authenticationManager, firstPagesAnnouncements]
  );

  const onTableChange = useCallback<TableChangeHandler<SiteAnnouncement>>(
    (type, newState) => {
      setPage(newState.page);
      if (type === 'sort') {
        setPublishTimeSort(
          newState.sortField === SiteAnnouncementListColumn.PUBLISH_TIME
            ? newState.sortOrder
            : undefined
        );
      }
    },
    []
  );

  const onShowDeleteConfirmModal = useCallback((ids: number[]) => {
    setDeleteAnnouncementIds(ids);
    setShowDeleteConfirmModal(true);
  }, []);

  const onHideDeleteConfirmModal = useCallback(() => {
    setDeleteAnnouncementIds([]);
    setShowDeleteConfirmModal(false);
  }, []);

  const onConfirmDelete = useCallback(() => {
    executeDelete(deleteAnnouncementIds);
    setDeleteAnnouncementIds([]);
    setShowDeleteConfirmModal(false);
  }, [executeDelete, deleteAnnouncementIds]);

  return {
    loading,
    isAdmin,
    searchString,
    filteredAnnouncements,
    selectedAnnouncements,
    columns,
    batchOperations,
    onTableChange,
    onClearSelect,
    onHandleSearch,
    onHandleSelect,
    onHandleSelectPage,
    markAnnouncementsAsRead,
    getAnnouncementRowClasses,
    detailAnnouncement,
    onShowDetail,
    onHideDetail,
    showDeleteConfirmModal,
    deleteAnnouncementIds,
    onShowDeleteConfirmModal,
    onHideDeleteConfirmModal,
    onConfirmDelete
  };
};
